import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  ChevronDownIcon,
  TagIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getTournaments, deleteTournament } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import TournamentModal from '../components/TournamentModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import TournamentCard from '../components/TournamentCard';
import TournamentDashboard from './TournamentDashboard';
import type { Tournament } from '../components/TournamentList';

interface TournamentsProps {
  onSelectTournament?: (tournament: Tournament) => void;
}

export default function Tournaments({ onSelectTournament }: TournamentsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filteredTournaments, setFilteredTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [viewingTournament, setViewingTournament] = useState<Tournament | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    filterTournaments();
  }, [tournaments, searchTerm, selectedStatus]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const { data, error } = await getTournaments();
      
      if (error) throw error;
      
      if (data) {
        const formattedTournaments = data.map(tournament => ({
          id: tournament.id,
          name: tournament.name,
          start_date: tournament.start_date,
          end_date: tournament.end_date,
          status: tournament.status,
          capacity: tournament.capacity,
          registration_fee: tournament.registration_fee,
          prize_pool: tournament.prize_pool,
          venue: tournament.venue || '',
          description: tournament.description || '',
          registration_open_date: tournament.registration_open_date ?? null,
          registration_close_date: tournament.registration_close_date ?? null,
          scoring_method: tournament.scoring_method ?? 'stroke_play_net'
        }));

        setTournaments(formattedTournaments);
        setFilteredTournaments(formattedTournaments);

        if (!viewingTournament && formattedTournaments.length > 0) {
          const latest = formattedTournaments[formattedTournaments.length - 1];
          setViewingTournament(latest);
          if (onSelectTournament) onSelectTournament(latest);
        }
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterTournaments = () => {
    let filtered = [...tournaments];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        tournament => 
          tournament.name.toLowerCase().includes(term) ||
          (tournament.venue && tournament.venue.toLowerCase().includes(term)) ||
          (tournament.description && tournament.description.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(tournament => tournament.status === selectedStatus);
    }
    
    setFilteredTournaments(filtered);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTournaments();
  };

  const handleEdit = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setIsModalOpen(true);
  };

  const handleDelete = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setIsDeleteModalOpen(true);
  };

  const handleViewDetails = (tournament: Tournament) => {
    if (onSelectTournament) {
      onSelectTournament(tournament);
    }
    setViewingTournament(tournament);
  };

  const confirmDelete = async () => {
    if (!selectedTournament) return;

    try {
      const { error: deleteError } = await deleteTournament(selectedTournament.id);
      if (deleteError) throw deleteError;
      await fetchTournaments();
      toast.success('Tournament deleted successfully');
    } catch (err) {
      console.error('Error deleting tournament:', err);
      toast.error('Failed to delete tournament');
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedTournament(null);
    }
  };

  // If viewing a specific tournament, show its dashboard
  if (viewingTournament) {
    return (
      <TournamentDashboard 
        tournament={viewingTournament} 
        onBack={() => setViewingTournament(null)} 
      />
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-golf-gray">Tournaments</h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleRefresh}
            className="p-2 text-gray-500 hover:text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
            disabled={refreshing}
          >
            <ArrowPathIcon className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Tournament</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tournaments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Statuses</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStatus('all');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Reset Filters
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : filteredTournaments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="max-w-md mx-auto">
            <TagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tournaments found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || selectedStatus !== 'all' 
                ? "No tournaments match your current filters. Try adjusting your search criteria."
                : "There are no tournaments in the system yet. Create your first tournament to get started."}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2 mx-auto"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Tournament</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}

      <TournamentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTournament(null);
        }}
        tournament={selectedTournament}
        onSuccess={fetchTournaments}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTournament(null);
        }}
        onConfirm={confirmDelete}
        title={selectedTournament?.name || ''}
      />
    </div>
  );
}